import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Input } from '@skipper/ui';
import { CalendarPicker } from '@components/form/calendar/CalendarPicker';
import dayjs, { Dayjs } from 'dayjs';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { SimpleDropdown } from '@components/common/dropdowns/SimpleDropdown';
import { Checkbox, ListBox } from '@components/base';
import { useCreateEventMutation, useUpdateEventMutation } from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useQueryClient } from 'react-query';

export const AddCalendarEventModal = ({
    type,
    coursesList,
    event,
    setFirstLoad,
    setEventsReload,
}: {
    type: 'create' | 'update';
    coursesList: any;
    event?: any;
    setFirstLoad?: any;
    setEventsReload?: any;
}) => {
    const [calendar, setCalendar] = useState(
        type === 'update'
            ? {
                  title: event?.course?.title,
                  iconColor: event?.course.iconColor,
                  courseId: event?.course?.id,
              }
            : null,
    );
    const [eventName, setEventName] = useState(type === 'update' ? event?.title : '');
    const [eventDate, setEventDate] = useState(() =>
        type === 'update' ? dayjs(event?.date) : dayjs().startOf('day'),
    );
    const [startTime, setStartTime] = useState(() =>
        type === 'update' ? event.timeStart : dayjs(eventDate).startOf('day'),
    );
    const [endTime, setEndTime] = useState(() =>
        type === 'update' ? event.timeEnd : dayjs(eventDate).endOf('day'),
    );
    const [details, setDetails] = useState(() => (type === 'update' ? event.details : ''));
    const [url, setUrl] = useState(() => (type === 'update' ? event.url : ''));

    const [allDay, setAllDay] = useState(() => {
        if (type === 'create') return true;
        const startTime = dayjs(event.timeStart).format('hh:mm a');
        const endTime = dayjs(event.timeEnd).format('hh:mm a');
        const startOfDay = dayjs(event.date).startOf('day').format('hh:mm a');
        const endOfDay = dayjs(event.date).endOf('day').format('hh:mm a');
        const AllDay = startTime === startOfDay && endTime === endOfDay;
        return AllDay;
    });

    useEffect(() => {
        if (allDay) {
            setStartTime(dayjs(eventDate).startOf('day'));
            setEndTime(dayjs(eventDate).endOf('day'));
        }
    }, [allDay, eventDate]);

    const { toggle, dispatchNotification } = useUniversalModalDispatch();

    const elementRef = useRef<HTMLDivElement | null>(null);
    useOnClickOutside(elementRef, () => {
        toggle(false);
    });

    const queryClient = useQueryClient();

    const createEventMutation = useCreateEventMutation(getClient('calendar'), {
        onSuccess: (data) => {
            if (data?.createEvent?.success) {
                queryClient.invalidateQueries('CalendarEvents', {
                    refetchInactive: true,
                });
                toggle(false);
                setFirstLoad(true);
                dispatchNotification({
                    title: 'Success',
                    text: 'Event created successfully',
                    type: 'success',
                });
            } else {
                dispatchNotification({
                    title: 'Error',
                    text: data.createEvent.errors || 'Error creating event',
                    type: 'danger',
                });
            }
        },
    });

    const updateEventMutation = useUpdateEventMutation(getClient('calendar'), {
        onSuccess: (data, isLoading) => {
            if (data?.updateEvent?.success) {
                queryClient.invalidateQueries('CalendarEvents', {
                    refetchInactive: true,
                });
                toggle(false);
                setEventsReload({
                    updating: true,
                    oldCourse: event?.course?.id,
                    newCourse: calendar.courseId,
                });
                dispatchNotification({
                    title: 'Success',
                    text: 'Event updated successfully',
                    type: 'success',
                });
            } else {
                dispatchNotification({
                    title: 'Error',
                    text: data.updateEvent.errors || 'Error updating event',
                    type: 'danger',
                });
            }
        },
    });

    return (
        <div className="fixed inset-0 overflow-y-auto flex h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <div
                    className="relative bg-white rounded-md p-5 w-[370px] md:w-[550px] font-medium text-sm"
                    ref={elementRef}>
                    <div className="flex justify-between items-center">
                        <h6 className="font-bold">
                            {type === 'create' ? 'Add Event' : 'Edit event'}
                        </h6>
                        <FontAwesomeIcon
                            icon={faXmark}
                            size="lg"
                            className="cursor-pointer"
                            onClick={() => toggle(false)}
                        />
                    </div>

                    <CalendarSelector
                        coursesList={coursesList}
                        calendar={calendar}
                        setCalendar={setCalendar}
                    />
                    <p className="text-sm leading-5 font-medium text-gray-700 mt-4 mb-1">
                        Event name
                    </p>
                    <Input
                        type="text"
                        id="eventName"
                        height="medium"
                        placeholder="Enter event name"
                        value={eventName}
                        onChange={(e) => {
                            setEventName(e.target.value);
                        }}
                    />
                    <p className="text-sm leading-5 font-medium text-gray-700 mt-4">Details</p>
                    <textarea
                        id="eventDetails"
                        onChange={(data) => setDetails(data.target.value)}
                        value={details}
                        placeholder="Enter event details, location, or other important information"
                        className="w-full border rounded-md border-gray-300 focus:outline-none focus:ring-1 focus:ring-teal-600 p-4 mt-1"
                        rows={5}
                        cols={50}></textarea>
                    <p className="text-sm leading-5 font-medium text-gray-700 mt-4 mb-1">Weblink</p>
                    <Input
                        type="text"
                        id="eventUrl"
                        height="medium"
                        value={url}
                        placeholder="Optional website link"
                        onChange={(e) => {
                            setUrl(e.target.value);
                        }}
                    />
                    <DateAndTimeSelectionRow
                        eventDate={eventDate}
                        setEventDate={setEventDate}
                        startTime={startTime}
                        endTime={endTime}
                        setStartTime={setStartTime}
                        setEndTime={setEndTime}
                        allDay={allDay}
                        setAllDay={setAllDay}
                    />
                    <Button
                        variant="primary"
                        size="medium"
                        className="mt-5 px-6 w-full"
                        disabled={
                            createEventMutation.isLoading ||
                            eventName.length === 0 ||
                            calendar === null ||
                            updateEventMutation.isLoading
                        }
                        onClick={() =>
                            type === 'update'
                                ? updateEventMutation.mutate({
                                      title: eventName,
                                      details: details,
                                      url: url,
                                      date: eventDate,
                                      timeStart: startTime,
                                      timeEnd: endTime,
                                      courseId: calendar.courseId,
                                      id: event.id,
                                  })
                                : createEventMutation.mutate({
                                      title: eventName,
                                      details: details,
                                      url: url,
                                      date: eventDate,
                                      timeStart: startTime,
                                      timeEnd: endTime,
                                      courseId: calendar.courseId,
                                  })
                        }>
                        {type === 'update' ? 'Save Event' : 'Add Event'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const CalendarSelector = ({
    coursesList,
    calendar,
    setCalendar,
}: {
    coursesList: any;
    calendar: any;
    setCalendar: React.Dispatch<React.SetStateAction<any>>;
}) => {
    return (
        <div className="mt-5">
            <SimpleDropdown
                id="calendarSelector"
                label="Select calendar"
                // value={(e) => e.value}
                value={calendar?.title || ''}
                // getValue={(v) => v?.value}
                onChange={(c) => {
                    setCalendar(c);
                }}
                placeholder="">
                {coursesList?.map((course) => {
                    return (
                        <ListBox.Option
                            id={course.id}
                            key={course.id}
                            value={{
                                title: course.course.title,
                                iconColor: course.course.iconColor,
                                courseId: course.course.id,
                            }}
                            disabled={false}>
                            {() => (
                                <CourseWithColorDot
                                    title={course?.course?.title}
                                    iconColor={course?.course?.iconColor}
                                />
                            )}
                        </ListBox.Option>
                    );
                })}
            </SimpleDropdown>
        </div>
    );
};

const CourseWithColorDot = ({ title, iconColor }: { title: string; iconColor: string }) => {
    return (
        <div className="ml-1 flex items-center justify-left cursor-pointer">
            <div
                className="h-2 w-2 rounded-full  mr-2"
                style={{
                    backgroundColor: iconColor ?? '#c61f50',
                }}
            />
            <p className="text-base font-medium text-passport whitespace-nowrap">{title}</p>
        </div>
    );
};

const DateAndTimeSelectionRow = ({
    eventDate,
    setEventDate,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    allDay,
    setAllDay,
}: {
    eventDate: dayjs.Dayjs;
    setEventDate: React.Dispatch<React.SetStateAction<Dayjs>>;
    startTime: number;
    endTime: number;
    setStartTime: React.Dispatch<React.SetStateAction<Dayjs>>;
    setEndTime: React.Dispatch<React.SetStateAction<Dayjs>>;
    allDay: boolean;
    setAllDay: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    let timeArray = [];
    let count = 0;
    let startOfDay = dayjs(eventDate).startOf('day').format('YYYY/MM/DD hh:mm A');
    while (count < 48) {
        timeArray.push(startOfDay);
        count++;
        startOfDay = dayjs(startOfDay).add(30, 'minute').format('YYYY/MM/DD hh:mm A');
    }

    useEffect(() => {
        if (
            dayjs(endTime).isBefore(dayjs(startTime)) ||
            dayjs(endTime).format('hh:mm A') === dayjs(startTime).format('hh:mm A')
        )
            setEndTime(dayjs(startTime).add(1, 'hour'));
    }, [startTime, endTime]);

    useEffect(() => {
        if (
            !allDay &&
            dayjs(eventDate).endOf('day').format('hh:mm a') === dayjs(endTime).format('hh:mm a')
        )
            setEndTime(dayjs(startTime).add(1, 'hour'));
    }, [startTime, allDay]);

    return (
        <div className="mt-5">
            <div className="flex justify-between items-center mt-2 text-gray-700 text-sm font-medium leading-5">
                <div className="w-[35%]">
                    {/* <p className="mb-1">Date</p> */}
                    <p className="">Date</p>
                    <div className="">
                        <CalendarPicker
                            type="assignDate"
                            initialDate={eventDate}
                            useRound
                            useSmall
                            selectTeal
                            onDayChange={(day) => {
                                setEventDate(dayjs(day).startOf('day'));
                            }}
                        />
                    </div>
                </div>
                <div className="w-[30%]">
                    <p className="mb-0">Start time</p>
                    <SimpleDropdown
                        id="timeStartSelect"
                        label=""
                        size="large"
                        value={allDay ? 'Select' : dayjs(startTime).format('hh:mm A')}
                        onChange={(c) => {
                            setAllDay(false);
                            setStartTime(dayjs(c));
                        }}
                        placeholder="">
                        {timeArray?.map((time) => {
                            return (
                                <ListBox.Option id={time} key={time} value={time} disabled={false}>
                                    {() => <div>{dayjs(time).format('hh:mm A')}</div>}
                                </ListBox.Option>
                            );
                        })}
                    </SimpleDropdown>
                </div>
                <div className="w-[30%]">
                    <p>End time</p>
                    <SimpleDropdown
                        id="timeEndSelect"
                        label=""
                        value={allDay ? 'Select' : dayjs(endTime).format('hh:mm A')}
                        onChange={(c) => {
                            setAllDay(false);
                            setEndTime(dayjs(c));
                        }}
                        placeholder="">
                        {timeArray?.map((time) => {
                            return (
                                <ListBox.Option id={time} key={time} value={time} disabled={false}>
                                    {() => <div>{dayjs(time).format('hh:mm A')}</div>}
                                </ListBox.Option>
                            );
                        })}
                    </SimpleDropdown>
                </div>
            </div>
            <div className="mt-5">
                <Checkbox
                    checked={allDay}
                    setChecked={() => {
                        setAllDay(!allDay);
                        if (!allDay) {
                            setStartTime(dayjs(eventDate).hour(7));
                            setEndTime(dayjs(eventDate).hour(8));
                        }
                    }}
                    label="All Day"
                    srLabel="All Day"
                    id="allDayCheckbox"
                    labelClassName="text-gray-700 whitespace-normal"
                    name="allDay"
                />
            </div>
        </div>
    );
};
